// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import SharedSelect from "../components/SharedSelect";
import { useNavigate } from "react-router-dom";
import { getUser } from "../auth/auth";
import { getAdminReports, reviewAdminReport } from "../api/admin";
import { useToast } from "../components/ToastProvider";
import "../styles/AdminReports.css";

export default function AdminReports() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const data = await getAdminReports();
      setReports(data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      if (error?.message?.toLowerCase?.().includes("forbidden")) {
        showToast("Unauthorized access", "error");
        navigate("/home");
        return;
      }
      showToast("Failed to load reports", "error");
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== "admin") {
      showToast("Unauthorized access", "error");
      navigate("/home");
      return;
    }

    fetchReports();
  }, [user, navigate, fetchReports, showToast]);

  const getFilteredReports = () => {
    if (filter === "all") return reports;
    return reports.filter(r => r.status === filter);
  };

  const formatActionLabel = (value) => {
    switch (value) {
      case "warn_user":
        return "Warn User";
      case "apply_strike":
        return "Apply Strike";
      case "suspend_user":
        return "Suspend User";
      case "ban_user":
        return "Ban User";
      case "mark_reviewed":
        return "Mark Reviewed";
      case "dismiss":
        return "Dismiss";
      case "needs_more_info":
        return "Needs More Info";
      default:
        return value ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) : "None";
    }
  };

  const handleSelectReport = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const handleAction = async (e) => {
    e.preventDefault();
    
    if (!actionType) {
      showToast("Please select an action type", "warning");
      return;
    }

    setSubmitting(true);

    try {
      const result = await reviewAdminReport(selectedReport.id, {
        action: actionType,
        notes: actionNotes
      });
      showToast(result?.message || "Action completed", "success");
      
      // Refresh reports
      await fetchReports();
      
      setShowModal(false);
      setSelectedReport(null);
      setActionType("");
      setActionNotes("");
    } catch (error) {
      console.error("Error processing action:", error);
      showToast(error.message || "Failed to process action", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-reports">
        <div className="loading">Loading reports...</div>
      </div>
    );
  }

  const filteredReports = getFilteredReports();

  return (
    <div className="admin-reports">
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate("/home")}>←</button>
        <div>
          <h1>User Reports Management</h1>
          <p>Review and action user reports</p>
        </div>
      </header>

      <div className="filter-bar">
        <button 
          className={filter === "pending" ? "active" : ""} 
          onClick={() => setFilter("pending")}
        >
          Pending ({reports.filter(r => r.status === "pending").length})
        </button>
        <button 
          className={filter === "in_review" ? "active" : ""} 
          onClick={() => setFilter("in_review")}
        >
          In Review ({reports.filter(r => r.status === "in_review").length})
        </button>
        <button 
          className={filter === "resolved" ? "active" : ""} 
          onClick={() => setFilter("resolved")}
        >
          Resolved ({reports.filter(r => r.status === "resolved").length})
        </button>
        <button 
          className={filter === "all" ? "active" : ""} 
          onClick={() => setFilter("all")}
        >
          All ({reports.length})
        </button>
      </div>

      <div className="reports-list">
        {filteredReports.length === 0 ? (
          <div className="empty-state">
            <p>No reports found</p>
          </div>
        ) : (
          filteredReports.map(report => (
            <div 
              key={report.id} 
              className={`report-card ${report.status}`}
              onClick={() => handleSelectReport(report)}
            >
              <div className="report-header">
                <span className="report-id">{report.id}</span>
                <span className={`status-badge ${report.status}`}>
                  {report.status.replace("_", " ").toUpperCase()}
                </span>
              </div>
              
              <div className="report-content">
                <div className="report-field">
                  <span className="label">Reporter:</span>
                  <span className="value">User ID: {report.reporterId}</span>
                </div>
                <div className="report-field">
                  <span className="label">Target User:</span>
                  <span className="value">User ID: {report.targetUserId}</span>
                </div>
                <div className="report-field">
                  <span className="label">Reason:</span>
                  <span className="value">{report.reason}</span>
                </div>
                <div className="report-field">
                  <span className="label">Submitted:</span>
                  <span className="value">
                    {new Date(report.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="report-footer">
                Click to review details
              </div>
            </div>
          ))
        )}
      </div>

      {/* Report Detail Modal */}
      {showModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Report Details</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="label">Report ID:</span>
                  <span className="value">{selectedReport.id}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`status-badge ${selectedReport.status}`}>
                    {selectedReport.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Reporter ID:</span>
                  <span className="value">{selectedReport.reporterId}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Target User ID:</span>
                  <span className="value">{selectedReport.targetUserId}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Reason:</span>
                  <span className="value">{selectedReport.reason}</span>
                </div>
                <div className="detail-row full-width">
                  <span className="label">Details:</span>
                  <p className="value">{selectedReport.details || "No additional details provided"}</p>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted:</span>
                  <span className="value">{new Date(selectedReport.createdAt).toLocaleString()}</span>
                </div>
                
                {selectedReport.reviewedAt && (
                  <>
                    <div className="detail-row">
                      <span className="label">Reviewed:</span>
                      <span className="value">{new Date(selectedReport.reviewedAt).toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Reviewed By:</span>
                      <span className="value">{selectedReport.reviewedBy}</span>
                    </div>
                  </>
                )}

                {selectedReport.action && (
                  <div className="detail-row full-width">
                    <span className="label">Action Taken:</span>
                    <span className="value">{formatActionLabel(selectedReport.action)}</span>
                  </div>
                )}
              </div>

              {/* Admin Action Form */}
              {selectedReport.status !== "resolved" && (
              <form className="action-form" onSubmit={handleAction}>
                <h3>Take Action</h3>
                
                <label>
                  Action Type:
                  <SharedSelect 
                    value={actionType} 
                    onChange={(e) => setActionType(e.target.value)}
                    required
                  >
                    <option value="">Select action...</option>
                    <option value="needs_more_info">Keep In Review</option>
                    <option value="mark_reviewed">Mark Reviewed</option>
                    <option value="dismiss">Dismiss Report</option>
                    <option value="warn_user">Warn User (No Ban)</option>
                    <option value="apply_strike">Apply Strike</option>
                    <option value="suspend_user">Suspend User (7 days)</option>
                    <option value="ban_user">Ban User (Permanent)</option>
                  </SharedSelect>
                </label>

                <label>
                  Notes (optional):
                  <textarea 
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    placeholder="Add internal notes about this action..."
                    rows="4"
                  />
                </label>

                <div className="action-buttons">
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Processing..." : "Submit Action"}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
